<template>
    <div v-if="currentProduct" class="product-configurator">
        <div class="product-configurator__options">
            <ProductConfiguratorField  
                v-for="(group,index) of attributeGroups"
                @change="setOption($event,index)"
                :key="group.alias"
                :more-info="group.info"
                :model="selectedAttributes[index]"
                :name="group.name"
                :attributes="group.attributes"
                :hide-disabled="index === 0"
            />
            <ProductConfiguratorField
                v-for="(group, index) of groupedExtras"
                @change="setExtras($event, index)"
                :key="group.alias"
                :more-info="group.info"
                :model="selectedExtras[index]"
                :name="group.name"
                :attributes="group.extras"
                :hide-disabled="index === 0"
            />
        </div>
        <div class="product-configurator__controls">
            <div class="row justify-content-between align-items-end gap-x-2">
                <div class="col-auto">
                    <div class="product-configurator__summary">
                        <transition name="slide-fade" mode="out-in">
                            <p v-if="currentPrice" :key="currentPrice" class="product-configurator__price">
                                {{ formattedCurrency(currentPrice) }}
                            </p>
                        </transition>
                        <span class="product-configurator__price-tagline">Excluding VAT and Delivery</span>
                        <span class="product-configurator__price-tagline">Free collection available</span>
                    </div>
                    <div class="form-field form-field--quantity quantity-field mt-2" v-if="!hideQuantity">
                        <span :class="['quantity-field__down', {'disabled': (minQuantity || 1) === quantity }]" @click="quantityDown">-</span>
                        <input class="quantity-field__input" 
                            v-model="quantity" 
                            type="number"
                            value="1" 
                            :min="minQuantity || 1" 
                            :max="maxQuantity > 0 ? maxQuantity : undefined"
                            @keyup="resetQuantity()" 
                            @focusout="resetQuantity()" 
                        />
                        <span :class="['quantity-field__up', {'disabled': (maxQuantity > 0 && maxQuantity === quantity)}]" @click="quantityUp">+</span>
                        <p v-if="quantityText" class="text-small ml-1 mt-auto">
                            ({{ quantityText }})
                        </p>
                    </div>
                </div>
                <div class="col-auto">
                    <AddToBasketModalButton :price="currentPrice" @click="submitSelection" :disabled="!canAddToBasket" :basket-url="basketUrl">
                        <template #productRecommendations>
                            <slot name="products"></slot>
                        </template>
                    </AddToBasketModalButton>
                </div>
            </div>
        </div>              
    </div>
</template>
<script>
const GetHttp = () => import(/* webpackChunkName: 'http' */ '../../../_http.js').then(v => v.default);
import { ref, onMounted, computed } from 'vue';
import isEqual from 'lodash/isEqual';
import { useProductStore } from '../../stores/_product-store.js';
import { storeToRefs } from "pinia";
import ProductConfiguratorField from './ProductConfiguratorField.vue'
import AddToBasketModalButton from './AddToBasketModalButton.vue'

export default { 
    emits: ["change"],
    components: {
        ProductConfiguratorField,
        AddToBasketModalButton
    },
    props: {
        productData: {
            type: Object,
            required: true
        },
        basketUrl: {
            type: String,
            required: true
        }
    },
    setup(props) {
        const store = useProductStore();
        // const extraCosts = ref(0);
        const http = ref(null);
        const attributeGroups = ref(null);
        const hideQuantity = ref(false);
        const hidePriceDifference = ref(false);
        const minQuantity = ref(0);
        const maxQuantity = ref(null);
        const basketData = ref({});
        const { isLoading, currentProduct, currentProductData, groupedExtras, quantity, variants, selectedAttributes, selectedExtras, extras, basketSummary } = storeToRefs(store);
        const basketModalOpen = ref(false);

        const pushViewItemEvent = () => {
            window.dataLayer = window.dataLayer || [];
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                'event': 'custom_view_item',
                'datalayer_event_name': 'view_item',
                'ecommerce': {
                    'value': currentPrice.value,
                    'currency': 'GBP',
                    'items': [
                        {
                            'item_id': currentProduct.value.sku,
                            'item_name': currentProduct.value.name,
                            'quantity': quantity.value
                        }
                    ]
                }
            });
        };

        const pushAddBasketEvent = () => {
            window.dataLayer = window.dataLayer || [];
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                'event': 'custom_add_to_cart',
                'datalayer_event_name': 'add_to_cart',
                'ecommerce': {
                    'value': currentPrice.value,
                    'currency': 'GBP',
                    'items': [
                        {
                            'item_id': currentProduct.value.sku,
                            'item_name': currentProduct.value.name,
                            'quantity': quantity.value
                        }
                    ]
                }
            });
        };

        onMounted(async () => {
            store.updateCurrentProductData(props.productData);

            if (!currentProductData) return;

            store.updateVariants(currentProductData.value.variants);
            store.updateCurrentProduct(currentProductData.value);
            store.updateGroupedExtras(currentProductData.value.extrasGroups);
            attributeGroups.value = currentProductData.value.attributeGroups;
            hidePriceDifference.value = currentProductData.value.hidePriceDifference;
            hideQuantity.value = currentProductData.value.hideQuantity;
            minQuantity.value = currentProductData.value.minQuantity;
            maxQuantity.value = currentProductData.value.maxQuantity;

            http.value = await GetHttp();

            if (variants && Array.isArray(variants.value) && variants.value.length > 0) {
                let initialVariant = variants.value[0];

                if (initialVariant) {
                    store.updateCurrentProduct(initialVariant);
                    store.updateSelectedAttributes(initialVariant.attributeAliases);
                }
            }

            resetQuantity();

            preSelectExtras();
        });

        const preSelectExtras = () => {
            if (groupedExtras.value) {
                let initialExtraAliases = [];

                for(let group of groupedExtras.value) {
                    initialExtraAliases.push(group.extras[0].alias);
                }

                store.updateSelectedExtras(initialExtraAliases);
            }
        };

        const extraCosts = computed(() => {
            let costs = 0;

            if(groupedExtras.value && selectedExtras.value) {
                for (const group of groupedExtras.value) {
                    const groupExtras = group.extras.filter(extra => selectedExtras.value.includes(extra.alias));

                    costs += groupExtras.reduce((acc, extra) => acc + (extra.syncedPrice || extra.price), 0);
                }
            }

            return costs;
        });

        const resetQuantity = () => {
            if (quantity.value <= minQuantity.value || 0) quantity.value = minQuantity.value || 1;

            if(maxQuantity.value) {
                if (maxQuantity.value > 0 && quantity.value > maxQuantity.value) {
                    store.updateQuantity(maxQuantity.value);
                }
            }
            
            pushViewItemEvent();
        };

        //Computed
        const currentPrice = computed(() => {
            return (currentProduct.value.price * quantity.value) + extraCosts.value;
        });

        const canAddToBasket = computed(() => {
            return currentProduct.value && quantity.value > 0;
        });

        const quantityText = computed(() => {
            let textArr = [];

            if (minQuantity.value) {
                textArr.push(`Min: ${minQuantity.value}`);
            }

            if (maxQuantity.value) {
                textArr.push(`Max: ${maxQuantity.value}`);
            }

            return textArr.join(', ');
        });

        const formattedCurrency = computed(() => {
            return (number) => {
                const locale = 'en-GB';
                const currency = 'GBP';
                return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(number);
            };
        });

        //Methods
        const quantityUp = () => {
            store.updateQuantity(parseInt(quantity.value) + 1);
            resetQuantity();
        };

        const quantityDown = () => {
            store.updateQuantity(parseInt(quantity.value) - 1);
            resetQuantity();
        };

        const setVariant = (closestVariant) => {
            let foundVariant = variants.value.find(v => isEqual(v.attributeAliases, selectedAttributes.value));

            if (foundVariant) {
                store.updateCurrentProduct(foundVariant);
                resetQuantity();
            } else if (closestVariant) {
                store.updateCurrentProduct(closestVariant);
                resetQuantity();
            }
        };

        const setOption = (value, index) => {
            if (index === 0) {
                // get a match on the first attribute 
                // get first variant from matching first attribute
                let closestVariant = variants.value.find(product => product.attributeAliases[0] === value);

                if (closestVariant) {
                    store.updateSelectedAttributes(closestVariant.attributeAliases);
                    setVariant(closestVariant);
                } else {
                    store.updateSelectedAttributeAtIndex(index, value);
                    setVariant();
                }
            } else {
                store.updateSelectedAttributeAtIndex(index, value);
                setVariant();
            }
        };

        const submitSelection = () => {
            if (!canAddToBasket.value) return;
            addToBasket();
        };

        const setExtras = (value, index) => {
            store.updateSelectedExtraAtIndex(index, value);
        };

        const addToBasket = async () => {
            store.updateIsLoading(true);

            if(groupedExtras.value && selectedExtras.value) {
                let quantity = 1;
                const processedExtras = [];

                if (hideQuantity.value && currentProductData.value.quantityAlias) {
                    let quantityGroup = currentProductData.value.attributeGroups.find(x => {
                        return isEqual(x.alias, currentProductData.value.quantityAlias);
                    });

                    if (quantityGroup) {
                        let selectedAttribute = quantityGroup.attributes.find(x => {
                        return currentProduct.value.attributeAliases.includes(x.alias);
                        });

                        quantity = Number(selectedAttribute.name);
                    }
                }

                for (const group of groupedExtras.value) {
                    const groupExtras = group.extras.filter(extra => selectedExtras.value.includes(extra.alias));

                    groupExtras.forEach(extra => {
                        console.log(extra);
                        processedExtras.push({
                            productId: extra.sku,
                            quantity: extra.multiplies ? quantity : 1,
                            name: extra.name
                        });
                    });

                    store.updateExtras(processedExtras);
                }
            }

            basketData.value = {
                productId: currentProductData.value.sku,
                variantId: currentProduct.value.sku,
                quantity: quantity.value,
                extras: extras.value,
            };

            try {
                const { data } = await http.value.post('/basket/addtobasket', basketData.value);
                if (data.message) {
                    console.error(data.message);
                } else {
                    store.basketSummary = data.basketSummary;
                    pushAddBasketEvent();
                }
            } catch (error) {
                console.error("An error occurred during the HTTP request:", error);
            }

            store.updateIsLoading(false);      
        };

        return {
            selectedExtras,
            currentProduct,
            selectedAttributes,
            quantity,
            groupedExtras,
            currentPrice,
            canAddToBasket,
            quantityText,
            hideQuantity,
            hidePriceDifference,
            attributeGroups,
            formattedCurrency,
            isLoading,
            minQuantity,
            maxQuantity,
            quantityUp,
            quantityDown,
            setOption,
            setExtras,
            submitSelection,
            basketModalOpen
        };
    }
}
</script>

<style>
.slide-fade-enter-active {
  transition: all .5s ease;
}

.slide-fade-leave-active {
  transition: all .25s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(.2rem);
  opacity: 0;
}
</style>